/**
 * While the agent is on a call with the person this record is about, a bar across the top says
 * so (ember, the desk's one colour for a live call), counts the call, puts the facts a caller
 * asks for in reach, and takes a note without scrolling to the activity card.
 *
 * The call comes from the desk's events (GET /calls/live), polled every five seconds.
 */
import { formatPhone } from "@tlcrm/shared";
import { useState, type ReactNode } from "react";

import { errorMessage } from "../../api/client";
import { useAddNote, useLiveCall } from "../../api/hooks";
import { useToast } from "../../components/Toasts";
import { duration } from "../../lib/format";
import { useNow } from "../../lib/useNow";

export function LiveCallBar({
  contactIds,
  phones = [],
  about,
  facts,
}: {
  /** The people this record is about; the bar shows when the live call is with one of them. */
  contactIds: string[];
  /** Their numbers, for a call the desk has not matched to a person yet. */
  phones?: (string | null)[];
  /** Where a note from the bar is filed. */
  about: { contactId?: string; leadId?: string; policyId?: string };
  facts?: ReactNode;
}) {
  const live = useLiveCall();
  const now = useNow(1000);
  const addNote = useAddNote();
  const toast = useToast();
  const [note, setNote] = useState("");
  const call = live.data;
  const ours =
    call &&
    ((call.contactId && contactIds.includes(call.contactId)) ||
      (call.phone && phones.includes(call.phone)));
  if (!call || !ours) return null;

  const save = async () => {
    if (!note.trim()) return;
    try {
      await addNote.mutateAsync({ body: note.trim(), ...about });
      setNote("");
      toast("Note saved to the timeline.");
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  return (
    <div className="livebar" role="status" aria-label="On a call with this person">
      <span className="livebar-state">
        <span className="livebar-dot" aria-hidden="true" />
        On a call
        <b className="mono">{duration((now - Date.parse(call.answeredAt)) / 1000)}</b>
      </span>
      <span className="livebar-meta">
        {call.direction === "inbound" ? "Inbound" : "Outbound"}
        {call.queue && ` · ${call.queue}`}
        {call.phone && <span className="mono"> · {formatPhone(call.phone)}</span>}
      </span>
      {facts && <span className="livebar-facts">{facts}</span>}
      <input
        className="field field-sm livebar-note"
        value={note}
        placeholder="Note while you talk, Enter to save"
        aria-label="Note on this call"
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void save()}
        disabled={addNote.isPending}
      />
    </div>
  );
}
